import * as Yup from "yup";
import dayjs from "dayjs";
import { MINIMUM_PREMIUM_AMOUNT, PAYMENT_DETAILS, POLICY_TYPE_DETAILS } from "../../../Shared/CommonConstant";

// Brisk certificates (new, "V2") — the form's pure rules. Display only: the server checks everything again and works
// out the money itself (backend shared/briskV2Rules.js).

export const MODE_NEW = "NEW";
export const MODE_POLICY = "POLICY";

export const initialValues = {
  totalPremium: "",
  CustomerName: "",
  MobileNo: "",
  EmailID: "",
  City: "",
  State: "",
  CustomerGender: "",
  CustomerDOB: "",
  NomineeName: "",
  NomineeGender: "",
  Relation: "",
  Make: "",
  Model: "",
  EngineNo: "",
  ChassisNo: "",
  RegistrationNo: "",
  PaymentMode: PAYMENT_DETAILS[0].value,
  policyType: POLICY_TYPE_DETAILS[0].value,
  Address_Line1: "",
  Address_Line2: "",
  PlanName: "",
};

/** The policy's PA amount can be used as the Total Premium: then it is given, and not edited. Below Brisk's minimum it cannot be sent as it is. */
export const policyGivesPremium = (paAmount) => Number(paAmount) >= MINIMUM_PREMIUM_AMOUNT;

/** What an issued certificate cannot change (Brisk's UpdateCertificate has no field for them). */
export const UPDATE_LOCKED_FIELDS = ["EngineNo", "ChassisNo", "State", "PlanName", "totalPremium", "policyType", "PaymentMode"];

/** Whole paise from what is typed ("244", "244.5"); null when it is not a plain amount with at most 2 decimals. */
export const toPaise = (value) => {
  const text = String(value === undefined || value === null ? "" : value).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const [rupees, fraction = ""] = text.split(".");
  return Number(rupees) * 100 + Number((fraction + "00").slice(0, 2));
};

export const paiseText = (paise) => {
  const p = Math.round(Number(paise) || 0);
  return `${Math.floor(p / 100)}.${String(p % 100).padStart(2, "0")}`;
};

/** Net and GST of a GST-inclusive Total Premium, as the server works them out (net + GST always = total). */
export const splitPremium = (total) => {
  const totalPaise = toPaise(total);
  if (totalPaise === null) return { net: "", gst: "" };
  const netPaise = Math.round((totalPaise * 100) / 118);
  return { net: paiseText(netPaise), gst: paiseText(totalPaise - netPaise) };
};

/** The page's own rules (same messages), plus the mobile and email formats the server checks. */
export const buildSchema = ({ minimumPremium = MINIMUM_PREMIUM_AMOUNT } = {}) =>
  Yup.object().shape({
    totalPremium: Yup.number()
      .typeError("Total Premium Required")
      .min(minimumPremium, `Total premium must be at least ${minimumPremium}`)
      .required("Total Premium Required"),
    CustomerName: Yup.string().required("Customer Name Required"),
    Address_Line1: Yup.string().required("Address Line 1 Required"),
    Address_Line2: Yup.string().required("Address Line 2 Required"),
    MobileNo: Yup.string()
      .required("Mobile Required")
      .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits"),
    EmailID: Yup.string().email("Invalid email format").required("Email Required"),
    City: Yup.string().required("City Required"),
    State: Yup.string().required("State Required"),
    CustomerGender: Yup.string().required("Gender Required"),
    CustomerDOB: Yup.string().required("Date of Birth Required"),
    NomineeName: Yup.string().required("Nominee Name Required"),
    NomineeGender: Yup.string().required("Nominee Gender Required"),
    Relation: Yup.string().required("Nominee Relationship Required"),
    Make: Yup.string().required("Make Required"),
    PlanName: Yup.string().required("Plan Name Required"),
    Model: Yup.string().required("Model Required"),
    EngineNo: Yup.string().required("Engine Number Required"),
    ChassisNo: Yup.string().required("Chassis Number Required"),
    RegistrationNo: Yup.string().trim().required("Registration Number Required"),
    PaymentMode: Yup.string().required("Payment Mode Required"),
    policyType: Yup.string().required("Policy Type Required"),
  });

/** The form a server answer carries (policy prefill, a saved draft, Brisk's details) as Formik values. */
export const formToValues = (form = {}) => {
  const values = { ...initialValues };
  for (const key of Object.keys(initialValues)) {
    if (form[key] !== undefined && form[key] !== null) values[key] = String(form[key]);
  }
  // Defaults the server's form may leave out are kept (the old page blanked them on a policy prefill).
  values.PaymentMode = form.PaymentMode || initialValues.PaymentMode;
  values.policyType = form.policyType || initialValues.policyType;
  return values;
};

/** A DOB value (Date, ISO, "YYYY-MM-DD") as the dayjs value the date picker shows, or null. */
export const dobForPicker = (value) => {
  if (!value) return null;
  const text = String(value);
  const day = /^\d{4}-\d{2}-\d{2}$/.test(text) ? dayjs(text, "YYYY-MM-DD") : dayjs(value);
  return day.isValid() ? day : null;
};

/** What is sent to the server: the values as typed; a picked date as an ISO string (India midnight, as the picker makes it). */
export const valuesToForm = (values) => {
  const out = {};
  for (const key of Object.keys(initialValues)) out[key] = values[key] === undefined || values[key] === null ? "" : values[key];
  if (values.CustomerDOB instanceof Date) out.CustomerDOB = values.CustomerDOB.toISOString();
  else if (values.CustomerDOB && typeof values.CustomerDOB === "object" && typeof values.CustomerDOB.toISOString === "function") {
    out.CustomerDOB = values.CustomerDOB.toISOString();
  }
  return out;
};

/** The same certificate again (for the request id): chassis, engine, amount, plan and where it came from. */
export const attemptFingerprint = (values, mode, onlinePolicyId) =>
  [mode, onlinePolicyId || "", String(values.ChassisNo || "").replace(/\s+/g, "").toUpperCase(), String(values.EngineNo || "").replace(/\s+/g, "").toUpperCase(), toPaise(values.totalPremium), String(values.PlanName || "").toUpperCase()].join("|");

/** The dropdown's text for one pending policy. */
export const policyOptionLabel = (p) =>
  [p.policyNo, p.customerName, p.registrationNo, p.paAmount ? `PA ₹${p.paAmount}` : ""].filter(Boolean).join(" · ");
